import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { watchPath } from '../../services/crmService';
import { PL_PATH, PL_VIEWS, priceRange, priceNum, toPocketListing } from '../../services/pocketListingService';
import { SHOW_SAMPLES, SAMPLE_SELLER } from '../../data/samplePipeline';
import { transactionListStyles as tx } from '../transactions';

// Residence Pocket Listing Database: abbreviated seller pipeline info from every agent.
// Reads only crm/pocketListings (no seller names, addresses, contact info, notes or est. price).
// The owning agent and Admin can click through to the full seller profile; others see the agent to contact.
const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const input = { boxSizing: 'border-box', padding: '10px 12px', minHeight: 44, border: '1px solid #ddd', borderRadius: 8, fontSize: 15, fontFamily: FONT };
const sampleBadge = { marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#5c4413', background: '#f6e7b4', border: '1px dashed #b98d2e', borderRadius: 10, padding: '1px 8px' };
const PRICE_BANDS = [
  { id: '', label: 'Any price' },
  { id: '0-500000', label: 'Under $500K' },
  { id: '500000-750000', label: '$500K to $750K' },
  { id: '750000-1000000', label: '$750K to $1M' },
  { id: '1000000-2000000', label: '$1M to $2M' },
  { id: '2000000-999999999', label: '$2M+' },
];

export default function PocketListings() {
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'Admin';
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [community, setCommunity] = useState('');
  const [floorPlan, setFloorPlan] = useState('');
  const [pool, setPool] = useState('');
  const [view, setView] = useState('');
  const [band, setBand] = useState('');
  useEffect(() => watchPath(PL_PATH, setRows), []);

  const all = useMemo(() => [
    ...rows,
    ...(SHOW_SAMPLES ? [{ ...toPocketListing(SAMPLE_SELLER, { uid: '', name: 'Kelly Olin', phone: '(239) 555-0100', email: 'kelly@example.com' }), id: 'sample-seller', sample: true }] : []),
  ], [rows]);
  const opts = (k) => [...new Set(all.map((r) => r[k]).filter(Boolean))].sort();
  const shown = useMemo(() => {
    const [lo, hi] = band ? band.split('-').map(Number) : [null, null];
    const text = q.trim().toLowerCase();
    return all.filter((r) => (!community || r.community === community) && (!floorPlan || r.floorPlan === floorPlan)
      && (!pool || r.pool === pool) && (!view || r.view === view)
      && (!band || (() => { const a = priceNum(r.priceMin) ?? priceNum(r.priceMax); const b = priceNum(r.priceMax) ?? a; return a != null && a < hi && b >= lo; })())
      && (!text || [r.community, r.floorPlan, r.pool, r.view, r.agentName].join(' ').toLowerCase().includes(text)));
  }, [all, q, community, floorPlan, pool, view, band]);
  const canOpen = (r) => !r.sample && (isAdmin || (r.agentId && r.agentId === user?.uid));
  const open = (r) => { if (r.sample) { navigate('/pipeline?open=sample-seller'); return; } if (canOpen(r)) navigate(`/pipeline?open=${r.sellerInterestId || r.id}`); };

  return (
    <div style={{ fontFamily: FONT, background: '#f5f5f5', color: '#1a1a1a', minHeight: '100vh', boxSizing: 'border-box', padding: 'clamp(12px, 3vw, 24px)' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800 }}>Residence Pocket Listing Database</h2>
      <p style={{ margin: '0 0 12px', color: '#7f7f7f', fontSize: 14 }}>Homes our team's sellers are thinking about selling. Contact the listed agent for details. Seller names and addresses are private to the agent.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input style={{ ...input, flex: '1 1 200px' }} placeholder="Search community, floor plan, agent..." value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        <select style={input} value={community} onChange={(e) => setCommunity(e.target.value)} aria-label="Community"><option value="">All communities</option>{opts('community').map((x) => <option key={x}>{x}</option>)}</select>
        <select style={input} value={floorPlan} onChange={(e) => setFloorPlan(e.target.value)} aria-label="Floor plan"><option value="">All floor plans</option>{opts('floorPlan').map((x) => <option key={x}>{x}</option>)}</select>
        <select style={input} value={pool} onChange={(e) => setPool(e.target.value)} aria-label="Pool"><option value="">Pool: any</option><option>Yes</option><option>No</option></select>
        <select style={input} value={view} onChange={(e) => setView(e.target.value)} aria-label="View"><option value="">Any view</option>{PL_VIEWS.map((x) => <option key={x}>{x}</option>)}</select>
        <select style={input} value={band} onChange={(e) => setBand(e.target.value)} aria-label="Price range">{PRICE_BANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}</select>
      </div>
      <div style={tx.tableContainer}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tx.table}>
            <thead style={tx.tableHeader}>
              <tr>{['Community', 'Floor plan', 'Pool', 'View', 'Price range', 'Agent'].map((h) => <th key={h} style={{ ...tx.tableCell, ...tx.headerCell }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={6} style={tx.tableCell}>No pocket listings match.</td></tr>}
              {shown.map((r) => (
                <tr key={r.id} style={{ ...tx.tableRow, cursor: canOpen(r) || r.sample ? 'pointer' : 'default' }} onClick={() => open(r)}>
                  <td style={tx.tableCell}><span style={tx.clientName}>{r.community}</span>{r.sample && <span style={sampleBadge}>Sample</span>}</td>
                  <td style={tx.tableCell}>{r.floorPlan}</td>
                  <td style={tx.tableCell}>{r.pool}</td>
                  <td style={tx.tableCell}>{r.view}</td>
                  <td style={tx.tableCell}>{priceRange(r.priceMin, r.priceMax)}</td>
                  <td style={tx.tableCell}>
                    <div style={{ fontWeight: 600 }}>{r.agentName}</div>
                    <div style={{ fontSize: 13 }}>{r.agentContact?.phone && <a href={`tel:${r.agentContact.phone}`} onClick={(e) => e.stopPropagation()}>{r.agentContact.phone}</a>}</div>
                    <div style={{ fontSize: 13 }}>{r.agentContact?.email && <a href={`mailto:${r.agentContact.email}`} onClick={(e) => e.stopPropagation()}>{r.agentContact.email}</a>}</div>
                    {canOpen(r) && <div style={{ fontSize: 12, color: '#8C6D1F' }}>Your seller: click to open</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
